import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useJobInterests() {
  const { user } = useAuth();
  const userId = user?.id;
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInterests = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from('job_interests')
      .select(`
        *,
        jobs:job_id (*)
      `)
      .eq('candidate_id', userId)
      .order('created_at', { ascending: false });
    if (data) setInterests(data);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchInterests();
  }, [fetchInterests]);

  async function swipeInterested(jobId, voucherId, message) {
    if (!user) return;
    if (interests.some(i => i.job_id === jobId)) return { error: null };
    const { data, error } = await supabase
      .from('job_interests')
      .insert({
        candidate_id: user.id,
        job_id: jobId,
        status: 'interested',
      })
      .select()
      .single();
    if (!error && data) {
      // Send candidate's note to the voucher if provided
      if (voucherId && message?.trim()) {
        await supabase.from('messages').insert({
          sender_id: user.id,
          recipient_id: voucherId,
          job_interest_id: data.id,
          content: message.trim(),
        });
      }
      await fetchInterests();
    }
    return { error };
  }

  async function swipePass(jobId) {
    if (!user) return;
    if (interests.some(i => i.job_id === jobId)) return { error: null };
    const { error } = await supabase
      .from('job_interests')
      .insert({
        candidate_id: user.id,
        job_id: jobId,
        status: 'passed',
      });
    if (!error) await fetchInterests();
    return { error };
  }

  // Subscribe to real-time updates on own interests
  function subscribeToUpdates(callback) {
    if (!user) return () => {};
    const channelName = `candidate-interests-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'job_interests',
        filter: `candidate_id=eq.${user.id}`,
      }, (payload) => {
        callback(payload);
        fetchInterests();
      })
      .subscribe((status, err) => {
        if (err && err.message?.includes('aborted')) return;
      });

    return () => {
      try { supabase.removeChannel(channel); } catch (_) {}
    };
  }

  return {
    interests,
    loading,
    fetchInterests,
    swipeInterested,
    swipePass,
    subscribeToUpdates,
  };
}
